/**
 * ProcessingOverlay — Loader overlay pour opérations longues (paiement Stripe).
 *
 * v1.12.679 — Affiche un overlay non-dismissible avec spinner + message
 * pendant le temps d'attente Stripe (changeSubscription, preview, etc.).
 *
 * Usage:
 *   import { showProcessing, hideProcessing } from './ProcessingOverlay';
 *   showProcessing('Paiement en cours...', 'Finalisation de votre abonnement');
 *   await stripeCall();
 *   hideProcessing();
 */

import React, { useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, StyleSheet, Platform, DeviceEventEmitter } from 'react-native';

let createPortal = null;
if (Platform.OS === 'web') {
  try { createPortal = require('react-dom').createPortal; } catch {}
}

export function showProcessing(title, message) {
  DeviceEventEmitter.emit('__processing_show__', { title, message });
}

export function hideProcessing() {
  DeviceEventEmitter.emit('__processing_hide__');
}

export function ProcessingOverlayHost() {
  const [state, setState] = useState(null); // { title, message } | null

  useEffect(() => {
    const subShow = DeviceEventEmitter.addListener('__processing_show__', (data) => setState(data || {}));
    const subHide = DeviceEventEmitter.addListener('__processing_hide__', () => setState(null));
    return () => { subShow.remove(); subHide.remove(); };
  }, []);

  if (!state || Platform.OS !== 'web') return null;

  const tree = (
    <View style={s.overlay} pointerEvents="auto">
      <View style={s.backdropGuard}
        onMouseDown={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch {} }}
        onMouseUp={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch {} }}
      />
      <View style={s.card}>
        <ActivityIndicator size="large" color="#9B7B5A" style={{ marginBottom: 18 }} />
        {!!state.title && <Text style={s.title}>{state.title}</Text>}
        {!!state.message && <Text style={s.message}>{state.message}</Text>}
        <Text style={s.hint}>Merci de patienter, ne fermez pas cette fenêtre.</Text>
      </View>
    </View>
  );

  if (createPortal && typeof document !== 'undefined' && document.body) {
    return createPortal(tree, document.body);
  }
  return tree;
}

const s = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2147483647,
    padding: 20,
  },
  backdropGuard: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 1,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingTop: 32,
    paddingBottom: 24,
    paddingHorizontal: 28,
    width: '100%',
    maxWidth: 380,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 10 },
    elevation: 12,
    zIndex: 2,
  },
  title: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1A1814',
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: 0.2,
  },
  message: {
    fontSize: 14,
    color: '#5F584F',
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 12,
  },
  hint: {
    fontSize: 11,
    color: '#9B9489',
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

export default ProcessingOverlayHost;
